import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Box, Container, Group, Text, useMantineTheme } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { IconMasksTheater } from '@tabler/icons-react';
import { tokenStore } from '@/auth/tokenStore';
import { decodeAccessToken } from '@/auth/tokenClaims';
import { useAuth } from '@/stores/authContext';

/**
 * Постоянный баннер имперсонированной сессии: от чьего имени работает оператор (цель) и кто реальный
 * актор. Признак имперсонации — маркер в tokenStore (claim `act` есть только в токене первого обмена,
 * после refresh не переиздаётся). «Выйти» = обычный logout — отдельного Identix-эндпоинта завершения нет.
 */
export default function ImpersonationBanner() {
  const impersonation = tokenStore.getImpersonation();
  return impersonation ? <Banner actor={impersonation.username} /> : null;
}

function Banner({ actor }: { readonly actor: string }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const theme = useMantineTheme();
  const { ref, height } = useElementSize();
  const target = decodeAccessToken(tokenStore.getAccess())?.preferredUsername ?? '';

  // Другие закреплённые блоки и переход по якорю отсчитывают от этой высоты, иначе баннер их перекрыл бы.
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--app-sticky-extra', `${height}px`);
    return () => {
      root.removeProperty('--app-sticky-extra');
    };
  }, [height]);

  // Закреплён под шапкой: оператор не должен терять признак чужой сессии там, где меняет данные.
  return (
    <Box ref={ref} bg="brand.8" c="white" pos="sticky" top={theme.other.headerHeight} className="on-dark" style={{ zIndex: 90 }}>
      <Container size="lg" py="xs">
        <Group gap="xs" wrap="wrap">
          <IconMasksTheater size={18} style={{ flexShrink: 0 }} />
          <Text size="sm" c="white" style={{ flex: '1 1 240px' }}>
            {t('app.impersonation.banner', { target, actor })}
          </Text>
          <Anchor component="button" type="button" size="sm" fw={600} c="white" underline="always" onClick={() => logout()}>
            {t('app.impersonation.exit')}
          </Anchor>
        </Group>
      </Container>
    </Box>
  );
}
